import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken } from "../../slices/staffSlice";
import { fetchRequests, accessFileRequest } from '../../Requests/getRequests';
import { Button } from "../../components";
import { authStaff } from '../../utils/constants';
import useConfigOptions from '../../hooks/useConfigOptions';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { show } from '../../slices/toastSlice';
import { useNavigate } from 'react-router-dom';

const POLL_INTERVAL_MS = 3000;

function OtherReports() {

    const accessToken = useSelector(selectAccessToken);
    const { reportTypes } = useConfigOptions();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [reportType, setReportType] = useState("");
    const [toDate, setToDate] = useState("");
    const [process, setProcess] = useState(false);
    const [jobStatus, setJobStatus] = useState(null); // null | 'queued' | 'running' | 'done' | 'failed'
    const [reportLink, setReportLink] = useState("");
    const [jobError, setJobError] = useState("");
    const pollRef = useRef(null);

    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const pollJobStatus = (jobId, headers) => {
        pollRef.current = setInterval(async () => {
            const statusRes = await fetchRequests(`/reports/staff/job-status/${jobId}`, "get", headers, {}, {});
            if (!statusRes || statusRes?.err) return;

            setJobStatus(statusRes.status);

            if (statusRes.status === 'done') {
                stopPolling();
                setProcess(false);
                setReportLink(statusRes.result || "");
                dispatch(show({ type: "success", state: true, message: "Report generated successfully" }));
            } else if (statusRes.status === 'failed') {
                stopPolling();
                setProcess(false);
                setJobError(statusRes.error || "Report generation failed");
                dispatch(show({ type: "error", state: true, message: statusRes.error || "Report generation failed" }));
            }
        }, POLL_INTERVAL_MS);
    };

    const handleRequest = async(e) => {
        e.preventDefault();
        setProcess(true);
        setJobStatus(null);
        setReportLink("");
        setJobError("");
        stopPolling();

        const params = { toDate };
        const url = `/reports/staff/${reportType}`;
        const headers = authStaff(accessToken);

        let res = await fetchRequests(url, "get", headers, params, {});
        if(res?.code === 401){
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        if(res?.err){
            setProcess(false);
            dispatch(show({ type: "error", state: true, message: `${res?.err}` }));
            return;
        }

        setJobStatus(res.status);
        pollJobStatus(res.jobId, headers);
    };

    const downloadReport = async() => {
        const url = `/reports/staff/getReportFile`;
        const headers = authStaff(accessToken);
        const name = reportLink.split("/")[1];

        let res = await accessFileRequest(url, headers, { name });
        if(!res || res.err || !res.data){
            dispatch(show({ type: "error", state: true, message: res?.err || "Could not download the report file." }));
            return;
        }

        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        const disposition = res.headers?.['content-disposition'];
        const fileName = disposition ? disposition.split('filename=')[1].replace(/["']/g, '') : name;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(fileUrl);
    };

    const statusLabel = { queued: 'Queued…', running: 'Generating…', done: 'Done', failed: 'Failed' };

    return (
        <tr>
            <td className='px-4 py-3'>
                <select name="report-type" id="report-type" className='form-input' required onChange={(e) => setReportType(e.target.value)}>
                    <option>-- select option --</option>
                    {reportTypes?.map((type) => (
                        <option key={type.id} value={type.value}>{type.name}</option>
                    ))}
                </select>
            </td>
            <td className='px-4 py-3'>
                <input type="date" name="to-date" id="to-date" min="2021-01-01" max="2040-01-01" className='form-input' required onChange={(e) => setToDate(e.target.value)} />
            </td>
            <td className='px-4 py-3 inline-flex items-center space-x-4'>
                <Button
                    btnText={"Generate"}
                    btnType="button"
                    btnClasses="form-button mt-2"
                    btnFunction={handleRequest}
                    processing={process}
                    disabled={reportType !== "" && toDate !== "" ? false : true}
                />

                {jobStatus && jobStatus !== 'done' && jobStatus !== 'failed' && (
                    <span className='mt-2 text-sm text-gray-500 animate-pulse'>{statusLabel[jobStatus]}</span>
                )}

                {jobStatus === 'failed' && (
                    <span className='mt-2 text-sm text-red-500'>{jobError || 'Failed'}</span>
                )}

                {jobStatus === 'done' && reportLink && (
                    <button type="button" className='mt-2 flex items-center justify-center px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200' onClick={downloadReport}>
                        <ArrowDownTrayIcon className="w-5 h-5 mr-2" /> Download
                    </button>
                )}
            </td>
        </tr>
    );
}

export default OtherReports;
